import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function DashboardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    api.get('/dashboard/stats').then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>로딩 중...</div>
  );

  const S = {
    wrap: { padding: 32, maxWidth: 1100, margin: '0 auto' },
    title: { fontSize: 24, fontWeight: 700, color: '#E6EDF3', marginBottom: 8 },
    sub: { fontSize: 14, color: '#6B7280', marginBottom: 32 },
    tabs: { display: 'flex', gap: 8, marginBottom: 32, borderBottom: '1px solid #2A3441', paddingBottom: 0 },
    tab: (active) => ({
      padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
      border: 'none', background: 'transparent',
      color: active ? '#00F0FF' : '#6B7280',
      borderBottom: active ? '2px solid #00F0FF' : '2px solid transparent',
      marginBottom: -1, transition: 'all .2s',
      fontFamily: "'Noto Sans KR', sans-serif",
    }),
    cards: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 32 },
    card: { padding: 24, borderRadius: 14, background: '#161B22', border: '1px solid #2A3441', textAlign: 'center' },
    cardNum: { fontSize: 36, fontWeight: 800, color: '#00F0FF', fontFamily: 'monospace' },
    cardLabel: { fontSize: 12, color: '#6B7280', marginTop: 6 },
    section: { background: '#161B22', borderRadius: 14, border: '1px solid #2A3441', padding: 24, marginBottom: 24 },
    sectionTitle: { fontSize: 16, fontWeight: 700, color: '#E6EDF3', marginBottom: 16 },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #2A344133', fontSize: 14 },
    rowLast: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', fontSize: 14 },
  };

  const maxUsage = Math.max(...(data?.dailyUsage || []).map(d => d.amount), 1);

  return (
    <div style={S.wrap}>
      <div style={S.title}>내 대시보드</div>
      <div style={S.sub}>가입일: {data?.joinedAt ? new Date(data.joinedAt).toLocaleDateString('ko-KR') : '-'}</div>

      <div style={S.tabs}>
        {[['overview','전체 현황'],['charge','충전 내역'],['usage','사용 내역']].map(([id,label]) => (
          <button key={id} style={S.tab(tab===id)} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div style={S.cards}>
            <div style={S.card}>
              <div style={S.cardNum}>🪙 {(data?.tokens || 0).toLocaleString()}</div>
              <div style={S.cardLabel}>보유 토큰</div>
            </div>
            <div style={S.card}>
              <div style={S.cardNum}>{data?.stats?.totalProjects || 0}</div>
              <div style={S.cardLabel}>총 프로젝트</div>
            </div>
            <div style={S.card}>
              <div style={S.cardNum}>{data?.stats?.shortformCount || 0}</div>
              <div style={S.cardLabel}>숏폼 생성</div>
            </div>
            <div style={S.card}>
              <div style={S.cardNum}>{data?.stats?.scriptCount || 0}</div>
              <div style={S.cardLabel}>대본 생성</div>
            </div>
          </div>

          <div style={S.section}>
            <div style={S.sectionTitle}>📊 최근 14일 토큰 사용량</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120, padding: '0 4px' }}>
              {(data?.dailyUsage || []).map((d, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ fontSize: 9, color: '#6B7280' }}>
                    {d.amount > 0 ? d.amount : ''}
                  </div>
                  <div style={{
                    width: '100%',
                    height: d.amount > 0 ? `${Math.max((d.amount / maxUsage) * 80, 4)}px` : '4px',
                    background: d.amount > 0 ? '#00F0FF' : '#2A3441',
                    borderRadius: 4,
                    transition: 'height .3s',
                  }}/>
                  <div style={{ fontSize: 9, color: '#6B7280', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                    {d.date.slice(5)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={S.section}>
            <div style={S.sectionTitle}>📋 작업 내역 요약</div>
            {[
              ['총 프로젝트', `${data?.stats?.totalProjects || 0}개`],
              ['편집 완료', `${data?.stats?.completedProjects || 0}개`],
              ['숏폼 생성', `${data?.stats?.shortformCount || 0}개`],
              ['대본 생성', `${data?.stats?.scriptCount || 0}개`],
            ].map(([label, value], i, arr) => (
              <div key={label} style={i === arr.length-1 ? S.rowLast : S.row}>
                <span style={{ color: '#C9D1D9' }}>{label}</span>
                <span style={{ color: '#00F0FF', fontWeight: 700 }}>{value}</span>
              </div>
            ))}
          </div>

          <button onClick={() => navigate('/tokens')} style={{
            padding: '12px 28px', background: '#00F0FF', color: '#000',
            border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer',
            fontFamily: "'Noto Sans KR', sans-serif",
          }}>
            🪙 토큰 충전하기
          </button>
        </>
      )}

      {tab === 'charge' && (
        <div style={S.section}>
          <div style={S.sectionTitle}>💳 충전 내역</div>
          {(data?.chargeHistory || []).length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6B7280', padding: 32 }}>충전 내역이 없습니다</div>
          ) : (data?.chargeHistory || []).map((h, i, arr) => (
            <div key={i} style={i === arr.length-1 ? S.rowLast : S.row}>
              <div>
                <div style={{ color: '#C9D1D9', fontSize: 14 }}>{h.description}</div>
                <div style={{ color: '#6B7280', fontSize: 11, marginTop: 2 }}>
                  {new Date(h.date).toLocaleString('ko-KR')}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#4ADE80', fontWeight: 700 }}>+{h.amount?.toLocaleString()} 토큰</div>
                <div style={{ color: '#6B7280', fontSize: 11 }}>잔액 {h.balance?.toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'usage' && (
        <div style={S.section}>
          <div style={S.sectionTitle}>📤 토큰 사용 내역</div>
          {(data?.useHistory || []).length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6B7280', padding: 32 }}>사용 내역이 없습니다</div>
          ) : (data?.useHistory || []).map((h, i, arr) => (
            <div key={i} style={i === arr.length-1 ? S.rowLast : S.row}>
              <div>
                <div style={{ color: '#C9D1D9', fontSize: 14 }}>{h.description}</div>
                <div style={{ color: '#6B7280', fontSize: 11, marginTop: 2 }}>
                  {new Date(h.date).toLocaleString('ko-KR')}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#F44336', fontWeight: 700 }}>{h.amount?.toLocaleString()} 토큰</div>
                <div style={{ color: '#6B7280', fontSize: 11 }}>잔액 {h.balance?.toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
